import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const PERSONAS = [
  {
    name: "The Student",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/11834ffa24901e655f2be91b2381f4e3e623178977a2df53f4669e9bf4466390.jpeg",
  },
  {
    name: "The Engineer",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/6854bc06b4eb48b2e3a64fb2726a4966a42fa05145396f25ae9786d90dc2f78a.jpeg",
  },
  {
    name: "The Designer",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/97190d40f9d83d5d761668b6b74e0cabbbb55478467c8295a12f53e7450ebe6c.jpeg",
  },
  {
    name: "The Farmer",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/f221e9cdd17dfe83ff37df812ee94398feefda804a13969b0db0305e2339a458.jpeg",
  },
  {
    name: "The Small Business Owner",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/b090ca960789dd13616f876ad8e97c835919e7c5c153b048a29fc7aeb350beba.jpeg",
  },
  {
    name: "The Woman Entrepreneur",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/367345acb50f4148555891b8b0e13a4c776967f6e176ef6f25624a2e182f1f9f.jpeg",
  },
  {
    name: "The Technology Founder",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/89080c14f91b0a38b483a5ac1346b1fedf6eb281dbf71ea2ca3fd6dc93de6800.jpeg",
  },
  {
    name: "The Manufacturing Entrepreneur",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/199a042061032f0a361b5ea3c0ae6e6eeb82e02600e0bca11977b507a732bdbd.jpeg",
  },
  {
    name: "The Digital Entrepreneur",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/c656769b9bc1ca33b70616edf22293ba093104e47826afdd28072adacecb7cd8.jpeg",
  },
  {
    name: "The Village Entrepreneur",
    image: "https://static.prod-images.emergentagent.com/jobs/d6040e5b-985f-42e2-8b96-4d5504284918/images/00ba5f08a7c249aa16293095737318f1126de2ed6347b5c5d1f1a2b2b80d2995.jpeg",
  },
];

export function NextGeneration() {
  return (
    <section data-testid="next-generation-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,136,0,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">India's Next Generation</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="next-generation-headline">
            EVERY ONE OF THEM <span className="text-gradient-saffron">HAS AN IDEA.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Different backgrounds. Different towns. Different dreams. Their ideas travel into
            the LaunchLab ecosystem — and come out as businesses.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="persona-grid">
          {PERSONAS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.55, delay: (i % 5) * 0.08 }}
              className="group relative rounded-2xl overflow-hidden border border-slate-800 hover:border-[#FF8800]/40 transition-colors duration-300"
              data-testid={`persona-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#06080E] via-transparent to-transparent" />
              <div className="absolute top-3 left-3 glass px-2.5 py-1 rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Sparkles className="w-3 h-3 text-[#FF8800]" />
                <span className="font-mono-tech text-[9px] tracking-[0.18em] text-slate-200 uppercase">Has an idea</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3.5">
                <p className="font-display font-semibold text-white text-sm sm:text-base leading-tight">{p.name}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-16 text-center space-y-4"
        >
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-gradient-hero" data-testid="next-generation-quote-1">
            "YOUR BACKGROUND DOES NOT DEFINE THE SIZE OF YOUR DREAM."
          </p>
          <p className="font-display text-xl sm:text-2xl font-bold text-gradient-saffron" data-testid="next-generation-quote-2">
            "YOUR IDEA CAN BE THE BEGINNING."
          </p>
        </motion.div>
      </div>
    </section>
  );
}
